import React, {useEffect, useMemo, useRef, useState} from "react";
import {BreadCrumb} from "primereact/breadcrumb";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputSwitch} from "primereact/inputswitch";
import {ProgressSpinner} from "primereact/progressspinner";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import {setMessage} from "../../../system/app-slice";
import {upsertWorkspaceProject} from "../../../system/workspace-graph-slice";
import SelectZofinoDateTime from "../../tasks/components/select-zofino-datetime";
import {formatPersianDate} from "../../../models/goal-update/goal-update-api";
import {
    CLOSED_PROJECT_STATES,
    OFF_TRACK_PATHS,
    OPEN_PROJECT_STATES,
    PROJECT_STATES,
    REMINDER_RHYTHMS,
    emptyProjectUpdate,
    fromProjectUpdate,
    projectUpdateApi,
    toProjectUpdateBody,
} from "../../../models/project-update/project-update-api";
import {WIDGET_CATEGORIES, WIDGET_LIBRARY, WidgetCard, widgetDefinition} from "./project-update-widgets";
import ProjectUpdateClosure from "./project-update-closure";
import ProjectUpdateViewDialog, {ProjectStateChip} from "./project-update-view-dialog";
import "../../goals/components/goal-updates.css";
import "./project-updates.css";
import {RichTextEditor, richTextToPlain} from "../../../system/components/rich-text";

// «به‌روزرسانی پروژه» page (docs/tasks/18-project-updates.md; design zofino-project-update-with-highlights.html,
// logic ZOFINO-PROJECT-UPDATE-LOGIC.md): state, state-specific fields, summary / achievements / obstacles / next steps,
// highlight widgets, drafts with autosave, history; a closed state opens the closing workflow.

const fa = (value) => Number(value || 0).toLocaleString("fa-IR");
const TONE_CLASS = {green: "pu-tone-green", orange: "pu-tone-orange", red: "pu-tone-red", gray: "pu-tone-gray"};
const toUnix = (value) => (value ? Math.floor(new Date(value).getTime() / 1000) : null);

function Field({label, required, error, children, hint}) {
    return (
        <div className="flex flex-column gap-2">
            <label className="text-sm font-bold flex align-items-center gap-1">{label}{required && <span className="pu-required">*</span>}</label>
            {children}
            {hint && !error && <span className="text-2xs gu-muted">{hint}</span>}
            {error && <span className="pu-field-error">{error}</span>}
        </div>
    );
}

function DateField({value, onChange}) {
    return (
        <div className="flex align-items-center border-1 border-200 border-round-lg p-1 px-2 bg-white">
            <SelectZofinoDateTime fullWidth selectedDateTime={{start: null, end: toUnix(value)}} onChange={(event) => {
                if (event.end) onChange(new Date(event.end.toUnix() * 1000).toISOString());
            }}/>
        </div>
    );
}

function HistoryCard({update, onOpen}) {
    const config = PROJECT_STATES[update.selectedState || update.projectStatus];
    return (
        <button type="button" className={`pu-history-card ${TONE_CLASS[config?.tone] || "pu-tone-gray"}`} onClick={onOpen}>
            <div className="flex align-items-center justify-content-between w-full gap-2">
                {config ? <ProjectStateChip state={update.selectedState || update.projectStatus}/> : <span className="gu-chip gu-tone-gray">شروع</span>}
                <span className="text-2xs gu-muted">{formatPersianDate(update.publishedAt || update.createdAt)}</span>
            </div>
            <span className="text-sm font-bold" style={{lineHeight: 1.8, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden"}}>{richTextToPlain(update.summary || update.briff) || "—"}</span>
            <div className="flex align-items-center justify-content-between w-full pt-2" style={{borderTop: "1px solid #f5f5f4"}}>
                <span className="text-xs font-bold" style={{color: "#e6531e"}}>مشاهده</span>
                {update.author?.member && (
                    <span className="flex align-items-center gap-2 text-2xs gu-muted">{update.author.member.fullname}<Avatar memberData={update.author.member} style={{width: "1.5rem", height: "1.5rem"}} className="text-3xs"/></span>
                )}
            </div>
        </button>
    );
}

function WidgetLibrary({visible, onHide, widgets, onAdd, onDragType}) {
    const [category, setCategory] = useState("all");
    const items = WIDGET_LIBRARY.filter((item) => category === "all" || item.category === category);
    return (
        <Dialog visible={visible} onHide={onHide} className="pu-library rtl" header={<span className="text-lg font-bold">افزودن ویجت به نکات برجسته</span>} dismissableMask draggable={false} modal={false} position="left">
            <div className="gu shabnam-font flex flex-column gap-3" dir="rtl">
                <p className="m-0 text-sm gu-muted">ویجت را بکشید و روی بخش نکات برجسته رها کنید، یا روی «افزودن» بزنید.</p>
                <div className="flex flex-wrap gap-2">
                    {WIDGET_CATEGORIES.map((item) => (
                        <button key={item.value} type="button" className={`pu-chip-button ${category === item.value ? "is-active" : ""}`} onClick={() => setCategory(item.value)}>{item.label}</button>
                    ))}
                </div>
                <div className="grid m-0">
                    {items.map((item) => {
                        const added = widgets.includes(item.type);
                        return (
                            <div key={item.type} className="col-12 md:col-6 p-1">
                                <div className={`pu-library-item ${added ? "is-added" : ""}`} draggable={!added} onDragStart={(event) => { event.dataTransfer.setData("text/plain", `new:${item.type}`); onDragType(item.type); }} onDragEnd={() => onDragType(null)}>
                                    <div className="flex align-items-center gap-2">
                                        <span className="pu-widget__icon"><Icon icon={`mi mi:${item.icon}`} className="text-lg"/></span>
                                        <strong className="text-sm">{item.title}</strong>
                                    </div>
                                    <p className="m-0 text-xs gu-muted" style={{lineHeight: 1.8}}>{item.description}</p>
                                    <button type="button" className="gu-soft-button gu-soft-button--primary align-self-end" style={{height: "2rem"}} disabled={added} onClick={() => onAdd(item.type)}>
                                        {added ? "اضافه شده" : "افزودن"}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <p className="m-0 text-2xs gu-muted">چیدمان ویجت‌ها پس از افزودن نیز با کشیدن و رها کردن قابل تغییر است و برای شما ذخیره می‌شود.</p>
            </div>
        </Dialog>
    );
}

function ProjectUpdateStatus() {
    const params = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const memberships = useMemo(() => workspaceMembershipData?.workspace?.workspace_memberships || [], [workspaceMembershipData]);
    const people = memberships.map((membership) => ({label: membership?.member?.fullname || membership?.member?.username || "—", value: membership.documentId}));

    const [context, setContext] = useState(null);
    const [failed, setFailed] = useState(false);
    const [form, setForm] = useState(null);
    const [widgets, setWidgets] = useState([]);
    const [draft, setDraft] = useState(null);
    const [savedAt, setSavedAt] = useState(null);
    const [errors, setErrors] = useState({});
    const [publishing, setPublishing] = useState(false);
    const [closureVisible, setClosureVisible] = useState(false);
    const [tab, setTab] = useState("history");
    const [libraryVisible, setLibraryVisible] = useState(false);
    const [viewing, setViewing] = useState(null);
    const [dragIndex, setDragIndex] = useState(null);
    const [dropIndex, setDropIndex] = useState(null);
    const [dragType, setDragType] = useState(null);
    const dirty = useRef(false);
    const saving = useRef(null);
    const layoutTimer = useRef(null);

    const load = async () => {
        try {
            const data = await projectUpdateApi.context(params.id);
            setContext(data);
            return data;
        } catch (error) {
            setFailed(true);
            return null;
        }
    };

    useEffect(() => {
        (async () => {
            const data = await load();
            if (!data) return;
            const draftId = searchParams.get("draft");
            const existing = draftId ? data.drafts.find((item) => item.documentId === draftId) : null;
            const requested = searchParams.get("status");
            if (existing) {
                setDraft(existing);
                setForm(fromProjectUpdate(existing));
                setWidgets(existing.widgets?.length ? existing.widgets : data.layout);
                setSavedAt(existing.updatedAt);
            } else {
                setForm(emptyProjectUpdate(PROJECT_STATES[requested] ? requested : (OPEN_PROJECT_STATES.includes(data.project.projectStatus) ? data.project.projectStatus : null)));
                setWidgets(data.layout);
            }
        })();
        // reload only for another project
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id]);

    const canPublish = Boolean(context?.canPublish);
    const projectClosed = Boolean(context?.project?.closedAt);
    const editable = canPublish && !projectClosed;

    // autosave the draft 1.5s after the last change
    useEffect(() => {
        if (!form || !dirty.current || !editable) return undefined;
        const timer = setTimeout(async () => {
            // a draft starts once something has been written (the preselected state alone is not a draft)
            if (![form.summary, form.accomplishments, form.obstacles, form.nextActions].some((value) => String(value || "").trim())) return;
            const body = toProjectUpdateBody(form, widgets);
            try {
                if (saving.current) await saving.current;
                const run = draft ? projectUpdateApi.saveDraft(draft.documentId, body) : projectUpdateApi.createDraft(params.id, body);
                saving.current = run;
                const saved = await run;
                setDraft(saved);
                setSavedAt(saved.updatedAt || new Date().toISOString());
                setContext((current) => current && ({...current, drafts: [saved, ...current.drafts.filter((item) => item.documentId !== saved.documentId)]}));
            } catch (error) {
                // the next change retries
            } finally {
                saving.current = null;
            }
        }, 1500);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, widgets]);

    const change = (patch) => {
        dirty.current = true;
        // errors belong to the state they were found for
        if (patch.selectedState !== undefined) setErrors({});
        setForm((current) => ({...current, ...patch}));
    };
    const changeDetails = (group, patch) => change({stateDetails: {...form.stateDetails, [group]: {...form.stateDetails[group], ...patch}}});
    const changeWidgets = (next) => {
        dirty.current = true;
        setWidgets(next);
        clearTimeout(layoutTimer.current);
        layoutTimer.current = setTimeout(() => projectUpdateApi.saveLayout(next).catch(() => null), 800);
    };
    const addWidget = (type) => {
        if (!widgets.includes(type)) changeWidgets([...widgets, type]);
    };
    const removeWidget = (type) => changeWidgets(widgets.filter((item) => item !== type));
    const dropOn = (index, event) => {
        event.preventDefault();
        const payload = event.dataTransfer.getData("text/plain");
        if (payload.startsWith("new:")) {
            const type = payload.slice(4);
            if (!widgets.includes(type)) changeWidgets([...widgets.slice(0, index), type, ...widgets.slice(index)]);
        } else if (dragIndex !== null && dragIndex !== index) {
            const next = [...widgets];
            const [moved] = next.splice(dragIndex, 1);
            next.splice(index > dragIndex ? index - 1 : index, 0, moved);
            changeWidgets(next);
        }
        setDragIndex(null);
        setDropIndex(null);
        setDragType(null);
    };

    const ensureDraft = async () => {
        const body = toProjectUpdateBody(form, widgets);
        if (saving.current) await saving.current;
        const saved = draft ? await projectUpdateApi.saveDraft(draft.documentId, body) : await projectUpdateApi.createDraft(params.id, body);
        setDraft(saved);
        return saved;
    };

    const publish = async () => {
        if (publishing || !editable) return;
        setPublishing(true);
        setErrors({});
        try {
            const current = await ensureDraft();
            const published = await projectUpdateApi.publish(current.documentId, toProjectUpdateBody(form, widgets));
            dirty.current = false;
            if (CLOSED_PROJECT_STATES.includes(published.selectedState)) {
                dispatch(upsertWorkspaceProject({...context.project, id: context.project.id, documentId: context.project.documentId, projectStatus: published.selectedState, closedAt: published.publishedAt}));
            } else {
                dispatch(upsertWorkspaceProject({id: context.project.id, documentId: context.project.documentId, projectStatus: published.selectedState}));
            }
            setClosureVisible(false);
            dispatch(setMessage({
                severity: "success",
                content: CLOSED_PROJECT_STATES.includes(published.selectedState)
                    ? "به‌روزرسانی منتشر و پروژه بسته شد."
                    : published.recipients?.length ? `به‌روزرسانی منتشر شد و برای ${fa(published.recipients.length)} نفر اعلان رفت.` : "به‌روزرسانی منتشر شد.",
            }));
            navigate(`/projects/${params.id}`);
        } catch (error) {
            const body = error?.response?.data || error?.data;
            const details = body?.error?.details?.errors;
            if (details) {
                setErrors(details);
                dispatch(setMessage({severity: "warn", content: "چند مورد لازم کامل نشده است؛ بخش‌های قرمز را کامل کنید."}));
                if (!Object.keys(details).some((key) => key.startsWith("closure."))) {
                    setClosureVisible(false);
                    setTimeout(() => document.querySelector(".pu-field-error")?.scrollIntoView({behavior: "smooth", block: "center"}), 100);
                }
            } else {
                const code = body?.error?.message;
                dispatch(setMessage({severity: "error", content: code === "project-update.error.close-permission-denied" ? "اجازه بستن این پروژه را ندارید." : "انتشار انجام نشد. دوباره تلاش کنید."}));
            }
        } finally {
            setPublishing(false);
        }
    };

    const submit = () => {
        if (CLOSED_PROJECT_STATES.includes(form.selectedState)) {
            setClosureVisible(true);
            return;
        }
        publish();
    };

    const saveDraftNow = async () => {
        try {
            await ensureDraft();
            setSavedAt(new Date().toISOString());
            dispatch(setMessage({severity: "success", content: "پیش‌نویس ذخیره شد."}));
            setClosureVisible(false);
        } catch (error) {
            dispatch(setMessage({severity: "error", content: "ذخیره پیش‌نویس انجام نشد."}));
        }
    };

    const openDraft = (item) => {
        setDraft(item);
        setForm(fromProjectUpdate(item));
        setWidgets(item.widgets?.length ? item.widgets : context.layout);
        setSavedAt(item.updatedAt);
        setErrors({});
        dirty.current = false;
    };
    const removeDraft = async (item) => {
        try {
            await projectUpdateApi.deleteDraft(item.documentId);
            setContext((current) => ({...current, drafts: current.drafts.filter((row) => row.documentId !== item.documentId)}));
            if (draft?.documentId === item.documentId) {
                setDraft(null);
                setSavedAt(null);
            }
        } catch (error) {
            dispatch(setMessage({severity: "error", content: "حذف پیش‌نویس انجام نشد."}));
        }
    };
    const newUpdate = () => {
        setDraft(null);
        setForm(emptyProjectUpdate());
        setWidgets(context.layout);
        setSavedAt(null);
        setErrors({});
        dirty.current = false;
    };

    const saveReminder = async (rhythm) => {
        setContext((current) => ({...current, project: {...current.project, reminderRhythm: rhythm}}));
        try {
            await projectUpdateApi.setReminder(params.id, rhythm);
            dispatch(setMessage({severity: "success", content: rhythm === "never" ? "یادآوری بروزرسانی خاموش شد." : "یادآوری بروزرسانی تنظیم شد."}));
        } catch (error) {
            dispatch(setMessage({severity: "error", content: "ذخیره یادآوری انجام نشد."}));
        }
    };

    if (failed) return <div className="h-full flex align-items-center justify-content-center text-sm">این پروژه پیدا نشد یا به آن دسترسی ندارید.</div>;
    if (!context || !form) return <div className="h-full flex align-items-center justify-content-center"><ProgressSpinner style={{width: "2.5rem", height: "2.5rem"}} strokeWidth="4"/></div>;

    const {project, metrics, suggestion} = context;
    const state = form.selectedState;
    const reminderOn = project.reminderRhythm && project.reminderRhythm !== "never";
    const suggestionDiffers = suggestion && state && OPEN_PROJECT_STATES.includes(state) && suggestion.state !== state;
    const legacy = (project.timeline || []).map((entry, index) => ({...entry, key: `legacy-${index}`, selectedState: entry.projectStatus, summary: entry.briff, createdAt: entry.createdAt}));
    const history = [...context.updates, ...legacy].sort((left, right) => new Date(right.publishedAt || right.createdAt || 0) - new Date(left.publishedAt || left.createdAt || 0));
    const author = memberships.find((item) => item.id === workspaceMembershipData?.id)?.member || workspaceMembershipData?.member;

    const stateCard = (key) => {
        const config = PROJECT_STATES[key];
        const active = state === key;
        const blocked = !editable || (!config.open && !context.canClose);
        return (
            <div key={key} className="col-4 p-1">
                <button type="button" className={`gu-status gu-status--${config.tone} ${active ? "is-active" : ""}`} disabled={blocked} onClick={() => change({selectedState: key})} aria-pressed={active} title={!config.open && !context.canClose ? "اجازه بستن پروژه را ندارید" : undefined}>
                    <span className="gu-status__icon"><Icon icon={`mi mi:${config.icon}`} className="text-2xl"/></span>
                    <span className="text-xs font-bold">{config.label}</span>
                </button>
            </div>
        );
    };
    // the summary is a long text (rich text editor); the other areas are lists, one item per line
    const area = (key, label, placeholder, required) => (
        <Field label={label} required={required} error={errors[key]} hint={key === "summary" ? null : "هر مورد در یک خط"}>
            {key === "summary"
                ? <RichTextEditor value={form[key]} placeholder={placeholder} readOnly={!editable} ariaLabel={label} onChange={(html) => change({[key]: html})}/>
                : <textarea className="gu-textarea" value={form[key]} placeholder={placeholder} disabled={!editable} onChange={(event) => change({[key]: event.target.value})}/>}
        </Field>
    );
    const detailText = (group, key, label, placeholder) => (
        <Field label={label} required error={errors[`stateDetails.${group}.${key}`]}>
            <RichTextEditor value={form.stateDetails[group][key] || ""} placeholder={placeholder} readOnly={!editable} ariaLabel={label} minHeight="4.5rem" onChange={(html) => changeDetails(group, {[key]: html})}/>
        </Field>
    );

    return (
        <div className="gu h-full overflow-auto shabnam-font" dir="rtl" style={{background: "#fafafa"}}>
            <div className="p-3 md:p-4 flex flex-column gap-4" style={{maxWidth: "88rem", margin: "0 auto"}}>
                <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                    <BreadCrumb
                        separatorIcon={<Icon icon="mi mi:chevron_left text-xs"/>}
                        className="bg-transparent border-none text-xs p-0"
                        model={[{label: "پروژه‌ها", command: () => navigate("/projects")}, {label: project.title, command: () => navigate(`/projects/${project.documentId}`)}, {label: "به‌روزرسانی پروژه"}]}
                    />
                    <div className="gu-reminder py-2">
                        <span className="inline-flex" style={{color: "#e6531e"}}><Icon icon="mi mi:notifications_active" className="text-lg"/></span>
                        <span className="text-xs font-bold">به من یادآوری کن بروزرسانی کنم</span>
                        <InputSwitch checked={Boolean(reminderOn)} disabled={!canPublish} onChange={(event) => saveReminder(event.value ? "weekly" : "never")}/>
                        {reminderOn && <Dropdown value={project.reminderRhythm} options={REMINDER_RHYTHMS} disabled={!canPublish} className="text-xs" onChange={(event) => saveReminder(event.value)}/>}
                    </div>
                </div>

                {projectClosed && <div className="gu-banner text-sm">این پروژه بسته شده است ({PROJECT_STATES[project.projectStatus]?.label})؛ به‌روزرسانی جدید برای آن منتشر نمی‌شود.</div>}
                {!canPublish && !projectClosed && <div className="gu-banner text-sm">فقط مسئول پروژه یا کسی که اجازه تغییر تنظیمات پروژه را دارد می‌تواند به‌روزرسانی منتشر کند.</div>}

                <div className="grid m-0">
                    <section className="col-12 xl:col-8 p-0 xl:pl-3 flex flex-column gap-3">
                        <div className="flex flex-wrap align-items-end justify-content-between gap-2">
                            <div>
                                <h1 className="m-0 text-2xl font-bold">{project.title}</h1>
                                <span className="text-sm gu-muted">به‌روزرسانی پروژه</span>
                            </div>
                            <span className="text-2xs gu-muted flex align-items-center gap-1">
                                {draft ? <><Icon icon="mi mi:cloud_done" className="text-base"/>پیش‌نویس · ذخیره خودکار {savedAt ? formatPersianDate(savedAt, true) : ""}</> : "پیش‌نویس با اولین تغییر ذخیره می‌شود"}
                            </span>
                        </div>

                        <div className="gu-card p-4 md:p-5 flex flex-column gap-5">
                            <div className="flex flex-column gap-3">
                                <span className="gu-section-title">وضعیت کلی{errors.selectedState && <span className="pu-field-error mr-2">{errors.selectedState}</span>}</span>
                                <span className="gu-group-label">پروژه‌های فعال</span>
                                <div className="grid m-0">{OPEN_PROJECT_STATES.map(stateCard)}</div>
                                <span className="gu-group-label">پروژه‌های بسته</span>
                                <div className="grid m-0">{CLOSED_PROJECT_STATES.map(stateCard)}</div>
                                {suggestion && (
                                    <div className={`pu-suggestion ${suggestionDiffers ? "pu-suggestion--warn" : ""}`}>
                                        <span className="inline-flex flex-none" style={{color: suggestionDiffers ? "#c2410c" : "#0f766e"}}><Icon icon="mi mi:auto_awesome" className="text-lg"/></span>
                                        <span className="flex-1">
                                            بر اساس داده‌های پروژه، وضعیت پیشنهادی «{PROJECT_STATES[suggestion.state]?.label}» است
                                            {suggestion.reasons?.length ? `: ${suggestion.reasons.join("، ")}.` : "."} تصمیم نهایی با شماست.
                                        </span>
                                        {editable && state !== suggestion.state && (
                                            <button type="button" className="pu-chip-button flex-none" onClick={() => change({selectedState: suggestion.state})}>انتخاب</button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {state === "on-way" && suggestion?.state === "out-of-way" && (
                                <div className="pu-details pu-details--mismatch">
                                    <span className="text-sm font-bold" style={{color: "#c2410c"}}>داده‌های پروژه بحرانی است؛ دلیل انتخاب «در مسیر» را بنویسید.</span>
                                    <Field label="دلیل انتخاب این وضعیت" required error={errors["stateDetails.mismatchReason"]}>
                                        <RichTextEditor value={form.stateDetails.mismatchReason} readOnly={!editable} ariaLabel="دلیل انتخاب این وضعیت" minHeight="4.5rem" onChange={(html) => change({stateDetails: {...form.stateDetails, mismatchReason: html}})}/>
                                    </Field>
                                </div>
                            )}

                            {state === "has-risks" && (
                                <div className="pu-details pu-details--risk">
                                    <span className="text-sm font-bold" style={{color: "#b45309"}}>ریسک و اقدام اصلاحی (برای انتشار لازم است)</span>
                                    {detailText("risk", "description", "شرح ریسک", "چه چیزی پروژه را تهدید می‌کند؟")}
                                    {detailText("risk", "impact", "اثر احتمالی ریسک", "اگر رخ دهد چه اتفاقی می‌افتد؟")}
                                    {detailText("risk", "action", "اقدام اصلاحی", "برای کنترل آن چه می‌کنیم؟")}
                                    <div className="grid m-0">
                                        <div className="col-12 md:col-6 p-0 md:pl-2">
                                            <Field label="مالک پیگیری ریسک" required error={errors["stateDetails.risk.owner"]}>
                                                <Dropdown value={form.stateDetails.risk.owner} options={people} filter placeholder="انتخاب عضو" className="w-full text-sm" disabled={!editable} onChange={(event) => changeDetails("risk", {owner: event.value})}/>
                                            </Field>
                                        </div>
                                        <div className="col-12 md:col-6 p-0 md:pr-2 mt-3 md:mt-0">
                                            <Field label="موعد بررسی مجدد" required error={errors["stateDetails.risk.reviewAt"]}>
                                                <DateField value={form.stateDetails.risk.reviewAt} onChange={(value) => changeDetails("risk", {reviewAt: value})}/>
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {state === "out-of-way" && (
                                <div className="pu-details pu-details--off">
                                    <span className="text-sm font-bold" style={{color: "#b91c1c"}}>علت، اثر و تصمیم اصلاحی (برای انتشار لازم است)</span>
                                    {detailText("offTrack", "cause", "علت خارج شدن از مسیر", "چرا پروژه با برنامه فعلی به خروجی نمی‌رسد؟")}
                                    <div className="grid m-0">
                                        <div className="col-12 md:col-6 p-0 md:pl-2">{detailText("offTrack", "projectImpact", "اثر روی خروجی پروژه", "")}</div>
                                        <div className="col-12 md:col-6 p-0 md:pr-2 mt-3 md:mt-0">{detailText("offTrack", "goalImpact", "اثر روی هدف متصل", "")}</div>
                                    </div>
                                    {detailText("offTrack", "decision", "تصمیم پیشنهادی", "")}
                                    <Field label="مسیر اصلاحی" required error={errors["stateDetails.offTrack.path"]}>
                                        <div className="flex flex-wrap gap-2">
                                            {OFF_TRACK_PATHS.map((path) => (
                                                <button key={path.value} type="button" disabled={!editable} className={`pu-chip-button ${form.stateDetails.offTrack.path === path.value ? "is-active" : ""}`} onClick={() => changeDetails("offTrack", {path: path.value})}>{path.label}</button>
                                            ))}
                                        </div>
                                    </Field>
                                    <div className="grid m-0">
                                        <div className="col-12 md:col-6 p-0 md:pl-2">
                                            <Field label="مالک تصمیم" required error={errors["stateDetails.offTrack.decisionOwner"]}>
                                                <Dropdown value={form.stateDetails.offTrack.decisionOwner} options={people} filter placeholder="انتخاب عضو" className="w-full text-sm" disabled={!editable} onChange={(event) => changeDetails("offTrack", {decisionOwner: event.value})}/>
                                            </Field>
                                        </div>
                                        <div className="col-12 md:col-6 p-0 md:pr-2 mt-3 md:mt-0">
                                            <Field label="تاریخ تصمیم‌گیری" required error={errors["stateDetails.offTrack.decisionAt"]}>
                                                <DateField value={form.stateDetails.offTrack.decisionAt} onChange={(value) => changeDetails("offTrack", {decisionAt: value})}/>
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {state && !PROJECT_STATES[state].open && (
                                <div className="gu-banner text-sm">با انتشار این به‌روزرسانی، پروژه بسته می‌شود. پس از «ارسال به‌روزرسانی»، کارها، مایلستون‌ها و اثر پروژه بر اهداف را تعیین تکلیف می‌کنید.</div>
                            )}

                            <div className="flex flex-column gap-4 pt-4" style={{borderTop: "1px solid #f3f4f6"}}>
                                {area("summary", "خلاصه وضعیت", "شرح مختصری از وضعیت فعلی پروژه بنویسید...", true)}
                                {area("accomplishments", "دستاوردها", "موفقیت‌های این دوره...")}
                                {area("obstacles", "موانع و چالش‌ها", "چه چیزهایی مانع پیشرفت هستند؟")}
                                {area("nextActions", "گام‌های بعدی و تصمیم‌ها", "اقدامات دوره بعد...")}
                            </div>

                            <div className="flex flex-column gap-3">
                                <span className="gu-section-title">نکات برجسته گزارش</span>
                                <div className="flex flex-column gap-3">
                                    {widgets.map((type, index) => (
                                        <div
                                            key={type}
                                            onDragOver={(event) => { event.preventDefault(); setDropIndex(index); }}
                                            onDrop={(event) => dropOn(index, event)}
                                        >
                                            <WidgetCard
                                                type={type}
                                                metrics={metrics}
                                                dragProps={{
                                                    draggable: editable,
                                                    onDragStart: (event) => { event.dataTransfer.setData("text/plain", `move:${index}`); setDragIndex(index); },
                                                    onDragEnd: () => { setDragIndex(null); setDropIndex(null); },
                                                    className: `pu-widget ${dragIndex === index ? "is-dragging" : ""} ${dropIndex === index && (dragIndex !== null || dragType) ? "is-drop-target" : ""}`,
                                                }}
                                                actions={editable && (
                                                    <span className="flex gap-1 flex-none">
                                                        <span className="pu-icon-button" title="جابه‌جا کنید"><Icon icon="mi mi:drag_indicator" className="text-lg"/></span>
                                                        <button type="button" className="pu-icon-button" aria-label={`حذف ${widgetDefinition(type)?.title}`} onClick={() => removeWidget(type)}><Icon icon="mi mi:close" className="text-lg"/></button>
                                                    </span>
                                                )}
                                            />
                                        </div>
                                    ))}
                                    {editable && (
                                        <button
                                            type="button"
                                            className={`pu-add-widget ${dragType ? "is-drop-target" : ""}`}
                                            onClick={() => setLibraryVisible(true)}
                                            onDragOver={(event) => event.preventDefault()}
                                            onDrop={(event) => dropOn(widgets.length, event)}
                                        >
                                            <span className="pu-add-widget__icon"><Icon icon="mi mi:add" className="text-2xl"/></span>
                                            <span className="flex flex-column gap-1">
                                                <strong className="text-sm">افزودن ویجت به گزارش</strong>
                                                <span className="text-xs gu-muted">نمودارها، جداول یا لیست‌های سفارشی برای گزارش حرفه‌ای‌تر</span>
                                            </span>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-wrap align-items-center justify-content-between gap-3 pt-3" style={{borderTop: "1px solid #f3f4f6"}}>
                                <span className="flex align-items-center gap-2 text-xs">
                                    {author && <Avatar memberData={author} style={{width: "2rem", height: "2rem"}} className="text-3xs"/>}
                                    <span className="flex flex-column"><strong>{author?.fullname || ""}</strong><span className="gu-muted text-2xs">ثبت‌کننده</span></span>
                                </span>
                                <div className="flex gap-2">
                                    <button type="button" className="gu-soft-button gu-soft-button--plain" onClick={() => navigate(`/projects/${project.documentId}`)}>انصراف</button>
                                    {editable && <button type="button" className="gu-soft-button gu-soft-button--gray" onClick={saveDraftNow}>ذخیره پیش‌نویس</button>}
                                    <button type="button" className="gu-soft-button gu-soft-button--solid px-5" disabled={!editable || publishing} style={{opacity: !editable || publishing ? .6 : 1}} onClick={submit}>
                                        <Icon icon="mi mi:send" className="text-lg"/>{publishing ? "در حال انتشار..." : "ارسال به‌روزرسانی"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>

                    <aside className="col-12 xl:col-4 p-0 mt-3 xl:mt-0">
                        <div className="gu-preview flex flex-column gap-3">
                            <div className="pu-tabs">
                                <button type="button" className={`pu-tab ${tab === "history" ? "is-active" : ""}`} onClick={() => setTab("history")}>به‌روزرسانی قبلی</button>
                                <button type="button" className={`pu-tab ${tab === "highlights" ? "is-active" : ""}`} onClick={() => setTab("highlights")}>نکات برجسته</button>
                                <button type="button" className={`pu-tab ${tab === "drafts" ? "is-active" : ""}`} onClick={() => setTab("drafts")}>پیش‌نویس‌ها {context.drafts.length > 0 && <span className="pu-tab__count">{fa(context.drafts.length)}</span>}</button>
                            </div>

                            {tab === "history" && (
                                <div className="flex flex-column gap-3" style={{maxHeight: "75vh", overflowY: "auto"}}>
                                    {history.length ? history.map((item) => (
                                        <HistoryCard key={item.documentId || item.key} update={item} onOpen={() => setViewing(item)}/>
                                    )) : <div className="gu-card p-3 text-sm gu-muted">هنوز به‌روزرسانی‌ای منتشر نشده است.</div>}
                                    <div className="flex align-items-center gap-2 text-2xs gu-muted px-2"><Icon icon="mi mi:flag" className="text-sm"/>شروع پروژه · {formatPersianDate(project.startAt)}</div>
                                </div>
                            )}

                            {tab === "highlights" && (
                                <div className="flex flex-column gap-3">
                                    <div className="gu-card p-3">
                                        <h3 className="m-0 text-sm font-bold">نکات برجسته پروژه</h3>
                                        <p className="m-0 mt-1 text-xs gu-muted">نمودارهایی که برای تصمیم‌گیری در این به‌روزرسانی نیاز دارید.</p>
                                        <div className="grid m-0 mt-3">
                                            {[
                                                {label: "پیشرفت", value: `${fa(metrics.progress)}٪`},
                                                {label: "برنامه تا امروز", value: metrics.planned === null ? "—" : `${fa(metrics.planned)}٪`},
                                                {label: "کار عقب‌افتاده", value: fa(metrics.tasks.overdue)},
                                                {label: "کار مسدود", value: fa(metrics.tasks.blocked)},
                                                {label: "روز تا پایان", value: metrics.daysLeft === null ? "—" : fa(metrics.daysLeft)},
                                                {label: "کار باز", value: fa(metrics.tasks.open)},
                                            ].map((tile) => (
                                                <div key={tile.label} className="col-4 p-1">
                                                    <div className="p-2 border-round-xl text-center" style={{background: "#fafaf9"}}>
                                                        <span className="block text-2xs gu-muted">{tile.label}</span>
                                                        <strong className="text-base">{tile.value}</strong>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    {editable && <button type="button" className="gu-soft-button gu-soft-button--primary justify-content-center" onClick={() => setLibraryVisible(true)}><Icon icon="mi mi:add" className="text-lg"/>افزودن ویجت</button>}
                                    {widgets.slice(0, 3).map((type) => <WidgetCard key={type} type={type} metrics={metrics} compact/>)}
                                </div>
                            )}

                            {tab === "drafts" && (
                                <div className="flex flex-column gap-3">
                                    {editable && <button type="button" className="gu-soft-button gu-soft-button--gray justify-content-center" onClick={newUpdate}><Icon icon="mi mi:add" className="text-lg"/>به‌روزرسانی جدید</button>}
                                    {context.drafts.length ? context.drafts.map((item) => (
                                        <div key={item.documentId} className={`gu-card p-3 flex flex-column gap-2 ${draft?.documentId === item.documentId ? "border-primary" : ""}`}>
                                            <div className="flex align-items-center justify-content-between gap-2">
                                                <span className="text-2xs gu-muted">{formatPersianDate(item.updatedAt, true)}</span>
                                                <span className="gu-chip gu-tone-gray">پیش‌نویس</span>
                                            </div>
                                            <strong className="text-sm" style={{lineHeight: 1.8}}>{item.summary || (item.selectedState ? PROJECT_STATES[item.selectedState]?.label : "بدون عنوان")}</strong>
                                            <div className="flex justify-content-between">
                                                <button type="button" className="border-none bg-transparent cursor-pointer text-xs font-bold p-0" style={{color: "#e6531e", font: "inherit"}} onClick={() => openDraft(item)}>
                                                    {draft?.documentId === item.documentId ? "در حال ویرایش" : "ادامه ویرایش"}
                                                </button>
                                                <button type="button" className="pu-icon-button" aria-label="حذف پیش‌نویس" onClick={() => removeDraft(item)}><Icon icon="mi mi:delete" className="text-lg"/></button>
                                            </div>
                                        </div>
                                    )) : <div className="gu-card p-3 text-sm gu-muted">پیش‌نویسی ندارید.</div>}
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
            </div>

            <WidgetLibrary visible={libraryVisible} onHide={() => setLibraryVisible(false)} widgets={widgets} onAdd={addWidget} onDragType={setDragType}/>
            <ProjectUpdateClosure
                visible={closureVisible}
                state={state}
                closure={form.closure}
                onChange={(closure) => change({closure})}
                context={context}
                memberships={memberships}
                errors={errors}
                publishing={publishing}
                onPublish={publish}
                onSaveDraft={saveDraftNow}
                onHide={() => setClosureVisible(false)}
            />
            <ProjectUpdateViewDialog
                visible={Boolean(viewing)}
                update={viewing?.documentId ? null : viewing}
                updateId={viewing?.documentId || null}
                project={project}
                onHide={() => setViewing(null)}
            />
        </div>
    );
}

export default ProjectUpdateStatus;
